import React, { useCallback, useEffect, useState } from 'react';
import { Gift, Plus, X, Trash2 } from 'lucide-react';
import {
  AdminStoreItemResource,
  AvailableSubscriptionResource,
  BundleDiscountType,
  SubscriptionBundleRequest,
  SubscriptionBundleResource,
} from '../types';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import Pagination from '../components/Pagination';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate } from '../utils/helpers';

const MONTH_OPTIONS = [1, 3, 6, 12, 24];

interface StoreItemRow {
  storeItemId: string;
  months: number | null;
}

interface BundleForm {
  name: string;
  description: string;
  isActive: boolean;
  discountType: BundleDiscountType;
  // Disimpan sebagai teks supaya isian bisa dikosongkan; kosong dikirim sebagai 0.
  discountValue: string;
  validFrom: string;
  validUntil: string;
  subscriptionId: string;
  subscriptionMonths: number;
  storeItems: StoreItemRow[];
}

const emptyForm = (): BundleForm => ({
  name: '',
  description: '',
  isActive: true,
  discountType: 'nominal',
  discountValue: '0',
  validFrom: '',
  validUntil: '',
  subscriptionId: '',
  subscriptionMonths: 12,
  storeItems: [],
});

const toRequest = (form: BundleForm): SubscriptionBundleRequest => ({
  name: form.name.trim(),
  description: form.description.trim() || null,
  isActive: form.isActive,
  discountType: form.discountType,
  discountValue: Number(form.discountValue) || 0,
  validFrom: form.validFrom || null,
  validUntil: form.validUntil || null,
  subscription: { subscriptionId: form.subscriptionId, months: form.subscriptionMonths },
  storeItems: form.storeItems.map((row) => ({ storeItemId: row.storeItemId, months: row.months })),
});

const firstErrorMessage = (err: any, fallback: string): string => {
  const errors = err?.response?.data?.errors;
  if (errors) {
    const first = Object.values(errors)[0];
    if (Array.isArray(first) && first.length) return String(first[0]);
  }
  return err?.response?.data?.message || fallback;
};

const periodLabel = (bundle: SubscriptionBundleResource) => {
  if (!bundle.validFrom && !bundle.validUntil) return 'Tanpa batas';
  return `${bundle.validFrom ? formatDate(bundle.validFrom) : '…'} – ${bundle.validUntil ? formatDate(bundle.validUntil) : '…'}`;
};

const contentLabel = (bundle: SubscriptionBundleResource) => {
  const sub = bundle.items.find((i) => i.itemType === 'subscription');
  const storeCount = bundle.items.filter((i) => i.itemType === 'store_item').length;
  return `${sub?.subscription?.name ?? '-'} ${sub?.months ?? ''} bln + ${storeCount} item`;
};

const Bundles: React.FC = () => {
  const { canManage } = useAuth();
  const [bundles, setBundles] = useState<SubscriptionBundleResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const perPage = 15;

  const [subscriptions, setSubscriptions] = useState<AvailableSubscriptionResource[]>([]);
  const [storeItems, setStoreItems] = useState<AdminStoreItemResource[]>([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<BundleForm>(emptyForm());
  const [pricing, setPricing] = useState<{ subtotal: number; discount: number; total: number } | null>(null);
  const [pricingError, setPricingError] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.getBundles(page, perPage, { search: search || undefined });
      setBundles(res.data);
      setTotal(res.meta.total);
    } catch (e) {
      console.error('Error loading bundles:', e);
      setBundles([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.getSubscriptions(1, 100).then((res) => setSubscriptions(res.data)).catch(() => setSubscriptions([]));
    api.getAdminStoreItems(1, 100, undefined, { isActive: true }).then((res) => setStoreItems(res.data)).catch(() => setStoreItems([]));
  }, []);

  // Panel harga: dihitung backend supaya rumus diskon periode & store item tidak terduplikasi di sini.
  useEffect(() => {
    if (!modalOpen) return;
    const ready = form.subscriptionId && form.storeItems.length > 0 && form.storeItems.every((r) => r.storeItemId);
    if (!ready) { setPricing(null); setPricingError(''); return; }
    const timer = setTimeout(() => {
      const request = toRequest(form);
      api.previewBundle({
        discountType: request.discountType,
        discountValue: request.discountValue,
        subscription: request.subscription,
        storeItems: request.storeItems,
      })
        .then((res) => { setPricing(res); setPricingError(''); })
        .catch((err) => { setPricing(null); setPricingError(firstErrorMessage(err, 'Harga tidak bisa dihitung.')); });
    }, 300);
    return () => clearTimeout(timer);
  }, [form, modalOpen]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm(), subscriptionId: subscriptions[0]?.id ?? '' });
    setError('');
    setPricing(null);
    setModalOpen(true);
  };

  const openEdit = (bundle: SubscriptionBundleResource) => {
    const sub = bundle.items.find((i) => i.itemType === 'subscription');
    setEditingId(bundle.id);
    setForm({
      name: bundle.name,
      description: bundle.description ?? '',
      isActive: bundle.isActive,
      discountType: bundle.discountType,
      discountValue: String(bundle.discountValue),
      validFrom: bundle.validFrom ?? '',
      validUntil: bundle.validUntil ?? '',
      subscriptionId: sub?.subscription?.id ?? '',
      subscriptionMonths: sub?.months ?? 12,
      storeItems: bundle.items
        .filter((i) => i.itemType === 'store_item')
        .map((i) => ({ storeItemId: i.storeItem?.id ?? '', months: i.months })),
    });
    setError('');
    setModalOpen(true);
  };

  const setStoreItemRow = (index: number, patch: Partial<StoreItemRow>) => {
    setForm((f) => ({ ...f, storeItems: f.storeItems.map((row, i) => (i === index ? { ...row, ...patch } : row)) }));
  };

  const onStoreItemChange = (index: number, storeItemId: string) => {
    const item = storeItems.find((s) => s.id === storeItemId);
    setStoreItemRow(index, { storeItemId, months: item?.durationType === 'lifetime' ? null : 12 });
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) { setError('Nama bundling wajib diisi.'); return; }
    if (!form.subscriptionId) { setError('Pilih paket langganan.'); return; }
    if (form.storeItems.length === 0) { setError('Tambahkan minimal satu store item.'); return; }
    try {
      setSaving(true);
      if (editingId) await api.updateBundle(editingId, toRequest(form));
      else await api.createBundle(toRequest(form));
      setModalOpen(false);
      await load();
    } catch (err: any) {
      setError(firstErrorMessage(err, 'Gagal menyimpan bundling.'));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (bundle: SubscriptionBundleResource) => {
    const next = !bundle.isActive;
    if (!next && !window.confirm(`Nonaktifkan "${bundle.name}"? Bundling hilang dari Pembelian Manual; pembelian yang sudah tercatat tidak berubah.`)) return;
    try { await api.toggleBundle(bundle.id, next); await load(); }
    catch (err: any) { window.alert(firstErrorMessage(err, 'Gagal mengubah status.')); }
  };

  const usedStoreItemIds = form.storeItems.map((r) => r.storeItemId);

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Gift className="h-6 w-6 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">Bundling</h1>
        </div>
        {canManage && (
          <button onClick={openCreate} className="inline-flex items-center gap-1 bg-primary-600 text-white rounded-md px-4 py-2 hover:bg-primary-700">
            <Plus className="h-4 w-4" /> Buat bundling
          </button>
        )}
      </div>

      <div className="mb-4">
        <input value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }} placeholder="Cari nama bundling..."
          className="w-full sm:w-80 border border-gray-300 rounded-md px-3 py-2" />
      </div>

      {loading ? <LoadingSpinner /> : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-2">Nama</th><th className="px-4 py-2">Isi</th><th className="px-4 py-2">Harga final</th>
                <th className="px-4 py-2">Periode</th><th className="px-4 py-2">Status</th><th className="px-4 py-2">Terjual</th>
                <th className="px-4 py-2">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {bundles.map((b) => (
                <tr key={b.id}>
                  <td className="px-4 py-2 font-medium text-gray-900">{b.name}</td>
                  <td className="px-4 py-2">{contentLabel(b)}</td>
                  <td className="px-4 py-2">
                    <div>{formatCurrency(b.pricing.total)}</div>
                    {b.pricing.discount > 0 && <div className="text-xs text-gray-400 line-through">{formatCurrency(b.pricing.subtotal)}</div>}
                  </td>
                  <td className="px-4 py-2">{periodLabel(b)}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${b.isAvailable ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {b.isActive ? (b.isAvailable ? 'Aktif' : 'Di luar periode') : 'Nonaktif'}
                    </span>
                  </td>
                  <td className="px-4 py-2">{b.manualPurchasesCount ?? 0}</td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {canManage && (
                      <>
                        <button onClick={() => openEdit(b)} className="text-primary-600 hover:underline mr-3">Ubah</button>
                        <button onClick={() => toggle(b)} className={b.isActive ? 'text-red-600 hover:underline' : 'text-green-700 hover:underline'}>
                          {b.isActive ? 'Nonaktifkan' : 'Aktifkan'}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {bundles.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">Belum ada bundling.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4">
        <Pagination currentPage={page} total={total} perPage={perPage} onPageChange={setPage} />
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-gray-500 bg-opacity-75 p-4">
          <form onSubmit={save} className="bg-white rounded-lg shadow-xl w-full max-w-4xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">{editingId ? 'Ubah bundling' : 'Buat bundling'}</h2>
              <button type="button" onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 px-6 py-5">
              <div className="lg:col-span-2 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nama bundling</label>
                  <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="Paket Pameran JIExpo" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deskripsi</label>
                  <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2}
                    className="w-full border border-gray-300 rounded-md px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Periode berlaku (opsional)</label>
                  <div className="flex items-center gap-2">
                    <input type="date" value={form.validFrom} onChange={(e) => setForm((f) => ({ ...f, validFrom: e.target.value }))}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2" />
                    <span className="text-sm text-gray-500">s/d</span>
                    <input type="date" value={form.validUntil} onChange={(e) => setForm((f) => ({ ...f, validUntil: e.target.value }))}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subscription (tepat 1 paket)</label>
                  <div className="flex gap-2">
                    <select value={form.subscriptionId} onChange={(e) => setForm((f) => ({ ...f, subscriptionId: e.target.value }))}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2">
                      <option value="">Pilih paket</option>
                      {subscriptions.map((s) => <option key={s.id} value={s.id}>{s.name} (level {s.level})</option>)}
                    </select>
                    <select value={form.subscriptionMonths} onChange={(e) => setForm((f) => ({ ...f, subscriptionMonths: Number(e.target.value) }))}
                      className="w-32 border border-gray-300 rounded-md px-3 py-2">
                      {MONTH_OPTIONS.map((m) => <option key={m} value={m}>{m} bulan</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Store item (minimal 1)</label>
                  <div className="space-y-2">
                    {form.storeItems.map((row, index) => {
                      const item = storeItems.find((s) => s.id === row.storeItemId);
                      return (
                        <div key={index} className="flex gap-2">
                          <select value={row.storeItemId} onChange={(e) => onStoreItemChange(index, e.target.value)}
                            className="flex-1 border border-gray-300 rounded-md px-3 py-2">
                            <option value="">Pilih item</option>
                            {storeItems.filter((s) => s.id === row.storeItemId || !usedStoreItemIds.includes(s.id)).map((s) => (
                              <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                          </select>
                          {item?.durationType === 'lifetime' ? (
                            <div className="w-32 border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-gray-600">Lifetime</div>
                          ) : (
                            <select value={row.months ?? 12} onChange={(e) => setStoreItemRow(index, { months: Number(e.target.value) })}
                              className="w-32 border border-gray-300 rounded-md px-3 py-2">
                              {MONTH_OPTIONS.map((m) => <option key={m} value={m}>{m} bulan</option>)}
                            </select>
                          )}
                          <button type="button" onClick={() => setForm((f) => ({ ...f, storeItems: f.storeItems.filter((_, i) => i !== index) }))}
                            className="text-gray-400 hover:text-red-600 px-2"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      );
                    })}
                  </div>
                  <button type="button" onClick={() => setForm((f) => ({ ...f, storeItems: [...f.storeItems, { storeItemId: '', months: 12 }] }))}
                    className="mt-2 text-sm font-medium text-primary-600 hover:underline">+ Tambah store item</button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Diskon bundling</label>
                  <div className="flex items-center gap-4">
                    {(['percent', 'nominal'] as BundleDiscountType[]).map((type) => (
                      <label key={type} className="inline-flex items-center gap-1 text-sm">
                        <input type="radio" checked={form.discountType === type} onChange={() => setForm((f) => ({ ...f, discountType: type }))} />
                        {type === 'percent' ? 'Persen' : 'Nominal'}
                      </label>
                    ))}
                    <input type="number" min={0} max={form.discountType === 'percent' ? 100 : undefined} value={form.discountValue}
                      onChange={(e) => setForm((f) => ({ ...f, discountValue: e.target.value }))}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                      placeholder={form.discountType === 'percent' ? '%' : 'Rp'} />
                  </div>
                </div>
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} />
                  Aktif
                </label>
              </div>

              <div>
                <div className="bg-gray-50 border rounded-lg p-4 text-sm space-y-2">
                  <h3 className="font-semibold text-gray-900 mb-2">Ringkasan harga</h3>
                  {pricing ? (
                    <>
                      <div className="flex justify-between"><span className="text-gray-500">Harga normal</span><span>{formatCurrency(pricing.subtotal)}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">Diskon bundling</span><span className="text-red-600">−{formatCurrency(pricing.discount)}</span></div>
                      <div className="flex justify-between border-t pt-2 font-semibold text-base"><span>Harga final</span><span>{formatCurrency(pricing.total)}</span></div>
                      {pricing.subtotal > 0 && pricing.discount > 0 && (
                        <div className="text-right text-xs text-gray-500">hemat {((pricing.discount / pricing.subtotal) * 100).toFixed(1).replace('.', ',')}%</div>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-500">{pricingError || 'Pilih paket dan minimal satu store item.'}</p>
                  )}
                </div>
                <p className="mt-3 text-xs text-gray-500">Maks 1 subscription. Level minimum store item tidak boleh di atas level paket di bundling.</p>
              </div>
            </div>

            {error && <div className="mx-6 mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">{error}</div>}

            <div className="flex justify-end gap-2 px-6 py-4 border-t">
              <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-md">Batal</button>
              <button type="submit" disabled={saving} className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50">
                {saving ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Bundles;
